import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GraduationCap, Library, Scale, Sparkles } from 'lucide-react';

import { Navbar, type NavDropdownItem } from '@/components/shared/Navbar';
import { MarketingNavDropdown } from './MarketingNavDropdown';
import { useFlagDecision } from '@/components/guards/FlagGate';

/**
 * The ROOT navbar — `/` only (src/pages/marketing/HomeVariantBPage.tsx).
 *
 * ── THE RULE THIS SHAPE COMES FROM ──────────────────────────────────────────
 * "The root domain sells the PATH; product pages sell the PRODUCT."
 * (Designer's "Nav Design" 3a, founder-approved 2026-09-11.)
 *
 * So the root has FOUR items — Law School ▾, Bar Prep ▾, Blog, About — and no
 * Features, no Pricing, no Outlines. Those belong to a product, and the root is
 * not a product page any more.
 *
 * ── THIS ANSWERS THE ANCHOR PROBLEM RATHER THAN FIXING IT ───────────────────
 * The old homepage nav was eight `/#<section>` anchors into HomePage.tsx. Three
 * of them (`#outlines`, `#study`, `#courses`) were dead in production for an
 * unknown period and nothing caught them, because an anchor into a page whose
 * sections move is fragile by construction. This navbar has exactly ONE anchor
 * — About — and every other destination is a REAL ROUTE that either exists or
 * 404s loudly.
 *
 * ── THINGS THAT ARE DECISIONS, NOT PREFERENCES ──────────────────────────────
 *
 * 1. YOUTUBE IS AN ICON, NOT A WORD. Founder decision. A text slot in the top
 *    nav sends qualified traffic off-site from the one position on the page
 *    where buying happens; the icon keeps the link without giving it a word's
 *    worth of weight.
 *
 * 2. COMMUNITY IS NOT IN THIS MENU, and it must not be added when
 *    `community-board` goes production-ON. Founder 2026-09-11: "Community can be
 *    in the sidebar nav within the app… that's taking them to the actual
 *    community. I don't need a link on the homepage." The in-app sidebar
 *    (navigation-config.ts, COMMUNITY group) is the surface that reaches the
 *    real thing. `/community` is also not a route — `grep -c 'path="/community"'
 *    src/App.tsx` returns 0 — so the item the design drew would have been a 404.
 *
 * 3. THE PACKS ITEM IS ON THE KILL SWITCH, and gating it here is
 *    .claude/rules/propagate-the-fix.md §2 applied by CONCEPT rather than by
 *    file. `/bar/ncbe-nextgen-questions` redirects to `/bar` when
 *    `nextgen-official-questions` is OFF (production: OFF), so an ungated nav
 *    item advertises a route that bounces the visitor back to where they came
 *    from — a loop that reads as a bug. HomeVariantBPage's matching TILE is
 *    gated on the same flag for the same reason; gating one and not the other
 *    is the half-fix that rule is about.
 *
 *    `useFlagDecision` rather than a local `useFlag`: there are FOUR flag states
 *    and the fourth is permanent. When VITE_UNLEASH_URL/TOKEN are absent at
 *    build time src/main.tsx omits <FlagProvider> entirely, `flagsReady` is
 *    pinned false and `flagsError` pinned null forever, so any
 *    `flagsReady || flagsError` guard is false for the life of the page.
 *    `useFlagDecision` leads on the build-time `unleashEnabled` constant and is
 *    the SAME decision <FlagGate> makes — one implementation, so this cannot
 *    re-derive the broken three-state version. <FlagGate> itself is not usable
 *    here: `navItems` is an array and a component cannot wrap an array element.
 *
 * 4. THE SCROLL-SPY WATCHES TWO IDS AND THAT IS THE WHOLE POINT.
 *    AskJDSNavbar's spy was written for HomePage.tsx's nine sections and guarded
 *    on `pathname !== '/'`. Pointing `/` at a different component would have
 *    switched that spy on against a section list belonging to a page that is no
 *    longer there — ids absent from the DOM get filtered out and the boundary
 *    walk hands their scroll range to whichever section is above them. Both
 *    existing gates stayed GREEN through that swap (the contract test is
 *    self-contained inside AskJDSNavbar; the drift checker resolved `/` through
 *    an anchorPages entry that also had to move). Board YJ7JpzxY7cXmKnxh1V1l.
 *
 *    So this spy lists only ids HomeVariantBPage really renders, and
 *    src/components/marketing/__tests__/MarketingRootNavbar.scroll-spy-contract.test.ts
 *    asserts that ACROSS FILES — against the page and the lazily-imported
 *    components it renders — which is the half neither previous gate could see.
 *
 *    `about-section` lives in the lazily-loaded AboutSection, so it is absent
 *    from the DOM until that chunk resolves; the null filter below handles it.
 *    Do NOT add a `display:none` or `hidden md:block` variant: a hidden element
 *    measures an all-zero rect, so its absolute top resolves to the CURRENT
 *    SCROLL POSITION and it migrates through the sorted order as the visitor
 *    scrolls, corrupting a different pair of boundaries at every offset.
 */

/** Ids this navbar's scroll-spy watches. Every one must be rendered by `/`. */
const SPIED_SECTIONS = ['top', 'about-section'];

/**
 * Trailing-slash-tolerant path normaliser, ONE definition for all four consumers
 * (the spy guard, isActive, handleHashLink, and the product-family match).
 *
 * It was declared inline in three of those and would have been declared a fourth
 * time for the family match. Same normaliser, same reason as every other navbar
 * in this repo: an exact compare makes `/law-school/` miss `/law-school`, and
 * because the compares short-circuit each other the symptom is a navbar that
 * looks permanently unvisited rather than an error.
 */
const strip = (p: string) => p.replace(/\/+$/, '') || '/';

/**
 * The YouTube glyph, inline rather than from lucide.
 *
 * `lucide-react` at the pinned version exports no `Youtube` icon (verified
 * against node_modules — the name does not appear in its .d.ts at all), and a
 * missing named import from that package is a COMPILE error rather than a
 * runtime one, so this is not a defensive choice. It is also the same path data
 * src/components/shared/Footer.tsx already renders, so the two marks match.
 */
const YouTubeGlyph = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
  </svg>
);

export function MarketingRootNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('top');

  const packsVisible = useFlagDecision('nextgen-official-questions').on;

  // Smooth-scroll on a hash arriving in the URL (deep link, back button).
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (location.hash) {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (id === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [location.hash]);

  // Scroll-based section detection. Root nav serves `/` only.
  useEffect(() => {
    if (strip(location.pathname) !== '/') return;

    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + window.innerHeight * 0.3;

      // Measure once, then order by MEASURED position — never by the order of
      // SPIED_SECTIONS. The boundary walk derives each section's bottom from the
      // NEXT entry's top, so a list disagreeing with the DOM silently produces
      // inverted ranges (top > bottom) that can never match. Sorting removes the
      // invariant rather than restating it, which is what stops a section
      // reorder from reintroducing the defect.
      const measured = SPIED_SECTIONS.map((id) => {
        const element = document.getElementById(id);
        return element
          ? { id, top: element.getBoundingClientRect().top + window.scrollY }
          : null;
      })
        .filter((item): item is { id: string; top: number } => item !== null)
        .sort((a, b) => a.top - b.top);

      const sectionBoundaries = measured.map(({ id, top }, idx) => ({
        id,
        top,
        bottom: idx === measured.length - 1 ? document.body.scrollHeight : measured[idx + 1].top,
      }));

      let activeId = 'top';
      for (const section of sectionBoundaries) {
        if (scrollPosition >= section.top && scrollPosition < section.bottom) {
          activeId = section.id;
          break;
        }
      }
      setActiveSection(activeId);
    };

    updateActiveSection();

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [location.pathname]);

  const isActive = (sectionId: string) => {
    if (strip(location.pathname) !== '/') return false;
    if (location.hash) return location.hash.replace('#', '') === sectionId;
    return activeSection === sectionId;
  };

  const handleHashLink = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const hash = href.split('#')[1];
    if (strip(location.pathname) === '/') {
      const element = document.getElementById(hash);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${hash}`);
    }
  };

  // ---------- Law School menu ----------
  // Products first, then a rule, then the non-product link. Community is
  // deliberately absent — see note 2.
  const lawSchoolItems: NavDropdownItem[] = [
    {
      label: 'JD Simplified',
      href: '/law-school',
      description: 'Free — no trial, no card',
      icon: <GraduationCap size={16} />,
    },
    {
      label: 'JDS Unlimited',
      href: '/law-school/unlimited',
      description: '$20 / month',
      icon: <Sparkles size={16} />,
    },
    {
      label: 'Outline Library',
      href: '/features/outlines',
      description: 'Full, Cram and Bar outlines for every subject',
      icon: <Library size={16} />,
      separatorBefore: true,
    },
  ];

  // ---------- Bar Prep menu ----------
  const barItems: NavDropdownItem[] = [
    {
      label: 'NextGen Practice',
      href: '/bar/practice',
      description: 'From $499',
      icon: <Scale size={16} />,
    },
    // See note 3 — kill switch, production OFF.
    ...(packsVisible
      ? [
          {
            label: 'Official NCBE Question Packs',
            href: '/bar/ncbe-nextgen-questions',
            description: 'Coming soon',
          } satisfies NavDropdownItem,
        ]
      : []),
    {
      label: 'NextGen Course',
      href: '/bar/course',
      description: 'March 2027',
    },
    {
      label: 'About the NextGen bar exam',
      href: '/bar',
      description: 'What the exam tests, and what changed',
      separatorBefore: true,
    },
  ];

  // Light a product menu when the visitor is INSIDE that family. The root nav
  // serves `/` today, so neither fires there — but the navbar is not pinned to
  // `/` by anything structural, and a dropdown whose `isActive` prop is never
  // passed is a prop that reads as wired and is not (the shape
  // .claude/rules/silent-degradation.md §1 is about: a value computed, or in
  // this case DECLARED, with no consumer). Matching on the segment boundary, not
  // the character, so a future `/barista` cannot light "Bar Prep".
  const inFamily = (prefix: string) => {
    const here = strip(location.pathname);
    return here === prefix || here.startsWith(`${prefix}/`);
  };

  const navItems = [
    {
      label: 'Law School',
      href: '#',
      isActive: inFamily('/law-school'),
      customElement: (
        <MarketingNavDropdown
          label="Law School"
          items={lawSchoolItems}
          isActive={inFamily('/law-school')}
        />
      ),
      mobileChildren: lawSchoolItems,
    },
    {
      label: 'Bar Prep',
      href: '#',
      isActive: inFamily('/bar'),
      customElement: (
        <MarketingNavDropdown label="Bar Prep" items={barItems} isActive={inFamily('/bar')} />
      ),
      mobileChildren: barItems,
    },
    {
      label: 'Blog',
      href: 'https://jdsimplified.com/blog/',
      isActive: false,
    },
    {
      label: 'About',
      href: '/#about-section',
      isActive: isActive('about-section'),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleHashLink(e, '/#about-section'),
    },
  ];

  return (
    <Navbar
      siteName="askjds"
      navItems={navItems}
      logoSize="md"
      dashboardText="Access"
      dashboardHref="/dashboard"
      utilityLinks={[
        {
          // THE URL IS A LITERAL, NOT A CONSTANT, AND THAT IS LOAD-BEARING.
          // scripts/check-nav-drift.mjs collects link targets from the AST by
          // reading `href:` property assignments whose value is a literal; an
          // identifier resolves to null and the link becomes INVISIBLE to the
          // checker. Hoisting this to a `const YOUTUBE_URL` (as an earlier draft
          // did) silently removed this surface's only outbound link from the
          // drift gate's coverage while leaving the spec declaring it — i.e. a
          // phantom MISSING, or worse, an unchecked link.
          href: 'https://www.youtube.com/@JDsimplified',
          label: 'YouTube',
          icon: <YouTubeGlyph />,
        },
      ]}
    />
  );
}

export default MarketingRootNavbar;
